import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getQuizById, submitQuiz } from '../../../api_service';

const parseOptions = (optionsJson) => {
  if (!optionsJson) return [];
  try {
    const arr = JSON.parse(optionsJson);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const TakeQuizModal = ({ item, traineeId, onClose, onSubmitted }) => {
  const quizId = item.quiz?.quizId;
  const [quiz, setQuiz] = useState(item.quiz || null);
  const [answers, setAnswers] = useState({}); // questionId -> answer
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // submitted attempt
  const [secondsLeft, setSecondsLeft] = useState(
    item.quiz?.timeLimitMinutes ? item.quiz.timeLimitMinutes * 60 : null
  );
  const submittedRef = useRef(false);

  // Fetch fresh quiz (full questions) on open
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getQuizById(quizId);
        const data = res?.data || res;
        if (data) {
          setQuiz(data);
          if (data.timeLimitMinutes && secondsLeft == null) {
            setSecondsLeft(data.timeLimitMinutes * 60);
          }
        }
      } catch {
        // fall back to item.quiz already in state
      }
    };
    if (quizId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  const setAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const doSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await submitQuiz(quizId, traineeId, { answersJson: JSON.stringify(answers) });
      setResult(res?.data || res);
    } catch {
      setError('Submission failed. Please try again.');
      submittedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [quizId, traineeId, answers]);

  // Countdown timer
  useEffect(() => {
    if (secondsLeft == null || result) return;
    if (secondsLeft <= 0) {
      doSubmit();
      return;
    }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, result, doSubmit]);

  const handleClose = () => {
    if (result) onSubmitted();
    onClose();
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const questions = quiz?.questions || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{quiz?.title}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {questions.length} questions &middot; {quiz?.totalMarks} marks
            </p>
          </div>
          <div className="flex items-center gap-4">
            {secondsLeft != null && !result && (
              <span className={`text-sm font-semibold ${secondsLeft <= 30 ? 'text-error' : 'text-foreground'}`}>
                <Icon name="Clock" size={14} className="inline mr-1" />
                {fmtTime(secondsLeft)}
              </span>
            )}
            <button onClick={handleClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
          </div>
        </div>

        <div className="overflow-y-auto p-6 space-y-6 flex-1">
          {result ? (
            <div className="text-center py-8">
              <Icon name="CheckCircle" size={56} className="text-success mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">Your Score</p>
              <p className="text-4xl font-bold text-foreground mt-1">
                {result.score ?? 0}/{quiz?.totalMarks}
              </p>
              <p className="text-sm text-muted-foreground mt-2">Quiz submitted successfully.</p>
            </div>
          ) : (
            questions.map((q, idx) => {
              const opts = parseOptions(q.optionsJson);
              return (
                <div key={q.questionId} className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">
                      {idx + 1}. {q.questionText}
                    </p>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{q.marks} mark{q.marks > 1 ? 's' : ''}</span>
                  </div>

                  {q.questionType === 'MCQ' ? (
                    <div className="space-y-2">
                      {opts.map((opt, oi) => (
                        <label
                          key={oi}
                          className="flex items-center gap-3 border border-border rounded-lg p-3 cursor-pointer hover:bg-muted/50"
                        >
                          <input
                            type="radio"
                            name={`q-${q.questionId}`}
                            checked={answers[q.questionId] === opt}
                            onChange={() => setAnswer(q.questionId, opt)}
                            className="accent-primary"
                          />
                          <span className="text-sm text-foreground">{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <input
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      value={answers[q.questionId] || ''}
                      onChange={e => setAnswer(q.questionId, e.target.value)}
                      onPaste={e => e.preventDefault()}
                      onCopy={e => e.preventDefault()}
                      placeholder="Type your answer..."
                    />
                  )}
                </div>
              );
            })
          )}

          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          {result ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={doSubmit} disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Quiz'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TakeQuizModal;
